// Live Data Status Registry
//
// Central pub-sub for the health of every long-lived Firestore subscription
// in the app. Hooks/services register themselves through
// `createReconnectingSubscription` (lib/database.ts) and update their state
// from within the onSnapshot callbacks. UI components subscribe via
// `useLiveDataStatus()` (built on React's useSyncExternalStore so it stays
// concurrent-mode safe and avoids tearing).
//
// Kept tiny on purpose:
//   - No React imports here so it can be used from non-React modules.
//   - The reconnector helper lives in `lib/database.ts` to avoid circular
//     imports with firestore primitives.

import { useSyncExternalStore } from 'react';

export type FeedState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface FeedStatus {
  readonly name: string;
  readonly state: FeedState;
  /** Last error code from Firestore, if any. */
  readonly errorCode?: string;
  /** Milliseconds until the next reconnect attempt while state === 'reconnecting'. */
  readonly retryDelayMs?: number;
  /** When the feed entered its current state (Date.now()). */
  readonly stateChangedAt: number;
  /** When the last good snapshot was delivered (Date.now()). 0 if never. */
  readonly lastSnapshotAt: number;
}

export interface FeedController {
  /** Force a re-subscribe right now (used by visibility/online handlers and the manual refresh button). */
  refresh: () => void;
}

interface InternalFeed extends FeedStatus {
  controller: FeedController;
}

type StatusListener = () => void;
type StatusChangeListener = (status: FeedStatus, prev: FeedStatus | null) => void;

class LiveDataStatusManagerImpl {
  private feeds = new Map<string, InternalFeed>();
  private listeners = new Set<StatusListener>();
  private changeListeners = new Set<StatusChangeListener>();

  /** Internal: register or update a feed's status. Called by the reconnector helper. */
  setStatus(name: string, partial: Omit<FeedStatus, 'name' | 'stateChangedAt' | 'lastSnapshotAt'> & {
    stateChangedAt?: number;
    lastSnapshotAt?: number;
  }, controller: FeedController): void {
    const prev = this.feeds.get(name) ?? null;
    const now = Date.now();
    const stateChanged = !prev || prev.state !== partial.state;
    const next: InternalFeed = {
      name,
      state: partial.state,
      errorCode: partial.errorCode,
      retryDelayMs: partial.retryDelayMs,
      stateChangedAt: stateChanged ? (partial.stateChangedAt ?? now) : prev!.stateChangedAt,
      lastSnapshotAt: partial.lastSnapshotAt ?? prev?.lastSnapshotAt ?? 0,
      controller,
    };
    this.feeds.set(name, next);
    // Always notify React listeners (cheap snapshot getter handles dedup).
    this.notify();
    // Only notify change-listeners on actual state transitions or on first registration.
    if (stateChanged) {
      const publicNext: FeedStatus = stripController(next);
      const publicPrev: FeedStatus | null = prev ? stripController(prev) : null;
      this.changeListeners.forEach(l => {
        try { l(publicNext, publicPrev); } catch (e) { console.error('[liveDataStatus] listener threw', e); }
      });
    }
  }

  /** Internal: called when a fresh snapshot arrives. Updates `lastSnapshotAt`. */
  markSnapshot(name: string, controller: FeedController): void {
    const prev = this.feeds.get(name);
    const now = Date.now();
    if (!prev) {
      // Feed wasn't registered yet (race). Register it as connected.
      this.feeds.set(name, {
        name,
        state: 'connected',
        stateChangedAt: now,
        lastSnapshotAt: now,
        controller,
      });
      this.notify();
      return;
    }
    if (prev.state !== 'connected') {
      // Transition into connected.
      this.setStatus(name, { state: 'connected', lastSnapshotAt: now }, controller);
      return;
    }
    // Already connected, just bump the snapshot timestamp.
    this.feeds.set(name, { ...prev, lastSnapshotAt: now });
    // No notify() here - lastSnapshotAt bump is not user-visible and would cause render storms.
  }

  /** Internal: remove a feed (called on hook unmount / final unsubscribe). */
  unregister(name: string): void {
    if (this.feeds.delete(name)) this.notify();
  }

  /** Public: snapshot of all current feed statuses (no controller refs). */
  getAll(): FeedStatus[] {
    return Array.from(this.feeds.values()).map(stripController);
  }

  /** Public: snapshot for useSyncExternalStore - same reference until something changes. */
  private cachedSnapshot: FeedStatus[] = [];
  private snapshotVersion = 0;
  private cachedVersion = -1;
  getSnapshot = (): FeedStatus[] => {
    if (this.snapshotVersion !== this.cachedVersion) {
      this.cachedSnapshot = this.getAll();
      this.cachedVersion = this.snapshotVersion;
    }
    return this.cachedSnapshot;
  };

  /** Public: subscribe React components to status changes via useSyncExternalStore. */
  subscribe = (listener: StatusListener): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Public: subscribe to ENTERING-a-new-state transitions only (used by App.tsx for audit logging). */
  onStateChange(listener: StatusChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => { this.changeListeners.delete(listener); };
  }

  /** Public: force every registered feed to re-subscribe right now. */
  refreshAll(): void {
    this.feeds.forEach(feed => {
      try { feed.controller.refresh(); } catch (e) { console.error(`[liveDataStatus] refresh(${feed.name}) threw`, e); }
    });
  }

  /**
   * Re-subscribe only feeds that are not healthy (reconnecting/offline).
   * Used on tab focus / online to avoid re-reading full collections when listeners are fine.
   */
  refreshUnhealthy(): void {
    this.feeds.forEach(feed => {
      if (feed.state !== 'reconnecting' && feed.state !== 'offline') return;
      try { feed.controller.refresh(); } catch (e) { console.error(`[liveDataStatus] refresh(${feed.name}) threw`, e); }
    });
  }

  private notify(): void {
    this.snapshotVersion++;
    this.listeners.forEach(l => {
      try { l(); } catch (e) { console.error('[liveDataStatus] listener threw', e); }
    });
  }
}

function stripController(f: InternalFeed): FeedStatus {
  return {
    name: f.name,
    state: f.state,
    errorCode: f.errorCode,
    retryDelayMs: f.retryDelayMs,
    stateChangedAt: f.stateChangedAt,
    lastSnapshotAt: f.lastSnapshotAt,
  };
}

export const liveDataStatus = new LiveDataStatusManagerImpl();

// ─── React hook ──────────────────────────────────────────────────────────────

/**
 * Subscribe a React component to every registered live-data feed's status.
 * Returns an array sorted by name. Re-renders only on state transitions, not on
 * every snapshot delivery (lastSnapshotAt bumps are silent).
 */
export function useLiveDataStatus(): FeedStatus[] {
  return useSyncExternalStore(liveDataStatus.subscribe, liveDataStatus.getSnapshot, liveDataStatus.getSnapshot);
}

/**
 * Roll up the array into a single aggregate state for the badge.
 *   - 'connected'    => all green
 *   - 'connecting'   => at least one initializing, none failed
 *   - 'reconnecting' => at least one currently retrying
 *   - 'offline'      => at least one offline (retried past the threshold)
 */
export function aggregateFeedState(feeds: FeedStatus[]): FeedState {
  if (feeds.length === 0) return 'connecting';
  let worst: FeedState = 'connected';
  const rank: Record<FeedState, number> = {
    connected: 0,
    connecting: 1,
    reconnecting: 2,
    offline: 3,
  };
  for (const f of feeds) {
    if (rank[f.state] > rank[worst]) worst = f.state;
  }
  return worst;
}
